import { type PropsWithChildren, useEffect, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import ReactCardFlip from 'react-card-flip';

import { LoyaltyTutorialSteps } from '../../../features/loyaltyProgramTutorial/constants';
import TutorialVipClubStatus from '../../../features/loyaltyProgramTutorial/TutorialVipClubStatus/TutorialVipClubStatus';
import { useLoyaltyProgramTutorial } from '../../../features/loyaltyProgramTutorial/useLoyaltyProgramTutorial';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';

import styles from './OldDesignHeaders.module.scss';

const DEFAULT_PROGRESS_COLOR = '#808080';
const CIRCLE_FLIP_INTERVAL = 5000;

const InnerCircle = (props: PropsWithChildren<{ progress: number; color?: string }>) => (
  <div
    className={styles.innerCircle}
    style={{
      '--progress-height': `${props.progress}%`,
      '--progress-color': props.color || DEFAULT_PROGRESS_COLOR,
    }}
  >
    {props.children}
  </div>
);

export const OldDesignHeaderCrownBadge = observer(() => {
  const { loyaltyProgramStore, uiStore } = useStores();
  const { showLoyaltyTutorial, tooltipStyles, setPopperElement, setReferenceElement } = useLoyaltyProgramTutorial(
    LoyaltyTutorialSteps.CHECK_CURRENT_STATUS,
    'bottom',
    [110, 22],
  );

  const [isFlipped, setIsFlipped] = useState(false);

  const { currentTier, points } = loyaltyProgramStore.userLoyalty ?? {};

  const progressPercentage = ((points || 0) / (currentTier?.pointsToComplete || 0)) * 100;
  const percentagesRounded = Math.floor(progressPercentage);

  const badgeClickHandler = () => {
    if (uiStore.openFullPageDialog === 'loyalty_program') {
      uiStore.closeFullPageDialog();
    } else {
      uiStore.setOpenFullPageDialog('loyalty_program', { from: 'header' });

      if (loyaltyProgramStore.tutorialStep === LoyaltyTutorialSteps.CHECK_CURRENT_STATUS) {
        loyaltyProgramStore.setTutorialNextStep();
      }
    }
  };

  const onClickBadge = () => {
    if (currentTier) {
      void analytics.sendEvent('loyalty_crown_button', {
        loyalty_club: currentTier.key,
        loyalty_percentage: progressPercentage,
      });
    }
    badgeClickHandler();
  };

  useEffect(() => {
    if (showLoyaltyTutorial) return;

    const interval = setInterval(() => {
      setIsFlipped((prevIsFlipped) => !prevIsFlipped);
    }, CIRCLE_FLIP_INTERVAL);

    return () => clearInterval(interval);
  }, [showLoyaltyTutorial]);

  const progress = showLoyaltyTutorial ? 50 : progressPercentage;

  return (
    <>
      <div
        ref={setReferenceElement}
        className={classNames(styles.crownBadge, showLoyaltyTutorial && styles.crownBadgeTutorial)}
        data-testid="crownBadge"
        onClick={onClickBadge}
      >
        <ReactCardFlip
          flipDirection="horizontal"
          flipSpeedBackToFront={1}
          flipSpeedFrontToBack={1}
          isFlipped={isFlipped}
        >
          <InnerCircle color={currentTier?.color} progress={progress}>
            <img alt="Crown" className={styles.crownIcon} src="/white-crown.svg" />
          </InnerCircle>
          <InnerCircle color={currentTier?.color} progress={progress}>
            <span className={styles.percentages}>{percentagesRounded}%</span>
          </InnerCircle>
        </ReactCardFlip>
      </div>
      {showLoyaltyTutorial && (
        <TutorialVipClubStatus
          handleClick={badgeClickHandler}
          setPopperElement={setPopperElement}
          tooltipStyles={tooltipStyles}
        />
      )}
    </>
  );
});
